/**
 * Opengater News (8001) admin hooks — articles + categories CRUD.
 * News paths are `/admin/...` (no /api/v1 prefix).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
const ARTICLES = '/admin/articles';
const CATEGORIES = '/admin/categories';
const UPLOAD = '/admin/uploads/file';
const A_KEY = ['og', 'news', 'articles'];
const C_KEY = ['og', 'news', 'categories'];
const PAGE = 100;
// ─── Articles ───────────────────────────────────────────────────────────────
export function useNewsArticles() {
    return useQuery({
        queryKey: A_KEY,
        queryFn: () => apiOg('news', `${ARTICLES}?include_inactive=true&limit=${PAGE}&offset=0`),
    });
}
export function useCreateNewsArticle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('news', ARTICLES, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: A_KEY }),
    });
}
export function useUpdateNewsArticle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('news', `${ARTICLES}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: A_KEY }),
    });
}
export function useDeleteNewsArticle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('news', `${ARTICLES}/${id}`, { method: 'DELETE' }),
        // The 8001 admin list is cached server-side and NOT invalidated on write, so
        // a refetch pulls the just-deleted row back ("deletion doesn't work"). Drop it
        // from the cache locally instead of refetching. Real fix = backend cache
        // invalidation on POST/PATCH/DELETE.
        onSuccess: (_data, id) => {
            qc.setQueryData(A_KEY, (old) => old
                ? { ...old, items: old.items.filter((a) => a.id !== id), total: Math.max(0, old.total - 1) }
                : old);
        },
    });
}
// ─── Categories ─────────────────────────────────────────────────────────────
export function useNewsCategories() {
    return useQuery({
        queryKey: C_KEY,
        queryFn: () => apiOg('news', `${CATEGORIES}?include_inactive=true`),
    });
}
export function useCreateNewsCategory() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('news', CATEGORIES, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: C_KEY }),
    });
}
export function useUpdateNewsCategory() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('news', `${CATEGORIES}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: C_KEY }),
    });
}
export function useDeleteNewsCategory() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('news', `${CATEGORIES}/${id}`, { method: 'DELETE' }),
        // Same cached-list caveat as articles — drop locally instead of refetching.
        onSuccess: (_data, id) => {
            qc.setQueryData(C_KEY, (old) => old ? old.filter((c) => c.id !== id) : old);
        },
    });
}
// ─── Images ─────────────────────────────────────────────────────────────────
/** Stores an image with the news service; resolves to the address the site loads it from. */
export async function uploadNewsImage(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiOg('news', UPLOAD, { method: 'POST', body: form });
    if (!res?.public_url)
        throw new Error('Сервер не вернул адрес картинки.');
    return res.public_url;
}
